import { Check, Loader, AlertCircle, AlertTriangle, Clock } from "lucide-react";
import { cn } from "@/utils";

export type StatusChipTone = "done" | "processing" | "failed" | "warning" | "queued";

export interface StatusChipProps {
  tone: StatusChipTone;
  children: React.ReactNode;
  className?: string;
  /** Hover tooltip — surfaces the longer explanation for the chip's state.
   *  Native browser tooltip; deliberately not a Radix Tooltip to keep the
   *  chip cheap to render inside list rows (one node per row × N rows). */
  title?: string;
}

const toneStyles: Record<StatusChipTone, string> = {
  done: "bg-[var(--success-bg)] text-[oklch(0.30_0.14_150)]",
  processing: "bg-[var(--primary-100)] text-[var(--primary-700)]",
  failed: "bg-[var(--destructive-bg)] text-[oklch(0.32_0.20_25)]",
  warning: "bg-[var(--warning-bg)] text-[oklch(0.32_0.14_75)]",
  queued: "bg-[var(--muted)] text-[var(--muted-foreground)]",
};

const ToneIcon: Record<StatusChipTone, React.ReactNode> = {
  done: <Check size={12} strokeWidth={2} aria-hidden />,
  processing: <Loader size={12} strokeWidth={2} aria-hidden className="animate-[spin_1.6s_linear_infinite]" />,
  failed: <AlertCircle size={12} strokeWidth={2} aria-hidden />,
  warning: <AlertTriangle size={12} strokeWidth={2} aria-hidden />,
  queued: <Clock size={12} strokeWidth={2} aria-hidden />,
};

/**
 * StatusChip — pill chip for async pipeline lifecycle states.
 * Spec: 22px height, radius-full, 11px font, 600 weight, 12px icon, 4px gap.
 * `processing` tone auto-spins the Loader icon at 1.6s.
 */
export function StatusChip({ tone, children, className, title }: StatusChipProps) {
  return (
    <span
      role="status"
      data-tone={tone}
      title={title}
      className={cn(
        "inline-flex items-center gap-1 px-2 rounded-[var(--radius-full)]",
        "text-[11px] font-semibold leading-none select-none",
        "h-[22px]",
        toneStyles[tone],
        className
      )}
    >
      {ToneIcon[tone]}
      {children}
    </span>
  );
}
